'use client';

import { forwardRef, useId, useLayoutEffect, useRef, useState } from 'react';

import cx from 'classnames';
import { motion } from 'motion/react';

import { type Masked, unmask } from '~/contentful/graphql';
import MinusIcon from '~/ui/assets/icons/minus.svg';
import PlusIcon from '~/ui/assets/icons/plus.svg';
import { Column, Grid, type GridProps } from '~/ui/components/grid';
import { Markdown } from '~/ui/components/markdown';
import { Breakpoint } from '~/ui/styles/grid';
import typographyStyles from '~/ui/styles/typography.module.scss';

import styles from './faq.module.scss';
import { type Faq_FaqFragment } from './query';

const MotionColumn = motion.create(Column);

export interface FaqProps extends GridProps {
  data: Masked<typeof Faq_FaqFragment>;
}

export const Faq = forwardRef(FaqImpl);
function FaqImpl({ data, ...props }: FaqProps, forwardedRef: React.ForwardedRef<HTMLDivElement>) {
  const { question, answer } = unmask<typeof Faq_FaqFragment>(data);

  const ddRef = useRef<HTMLElement>(null);
  const id = useId();
  const [isExpanded, setIsExpanded] = useState(false);

  useLayoutEffect(() => {
    const el = ddRef.current;
    if (!el) {
      return;
    }
    const onBeforeMatch = () => {
      setIsExpanded(true);
    };
    el.addEventListener('beforematch', onBeforeMatch);
    return () => el.removeEventListener('beforematch', onBeforeMatch);
  }, [setIsExpanded]);

  return (
    <Grid ref={forwardedRef} {...props}>
      <Column render={<dt />} className={typographyStyles.bodyLarge}>
        <Grid
          render={<button type="button" />}
          onClick={() => setIsExpanded(prev => !prev)}
          id={`${id}-header`}
          aria-expanded={isExpanded}
          aria-controls={`${id}-content`}
          className={styles.headerButton}
        >
          <Column
            span={{ [Breakpoint.MOBILE]: 4, [Breakpoint.TABLET]: 5, [Breakpoint.DESKTOP]: 5 }}
            className={styles.headerTextColumn}
          >
            {question}
          </Column>
          <Column
            span={{ [Breakpoint.MOBILE]: 1, [Breakpoint.TABLET]: 1, [Breakpoint.DESKTOP]: 1 }}
            offsetLeft={{ [Breakpoint.TABLET]: 1, [Breakpoint.DESKTOP]: 1 }}
            className={styles.iconWrapper}
          >
            {isExpanded ? (
              <MinusIcon className={styles.icon} />
            ) : (
              <PlusIcon className={styles.icon} />
            )}
          </Column>
        </Grid>
      </Column>

      <MotionColumn
        initial={{ overflow: 'hidden' }}
        animate={{ height: isExpanded ? 'auto' : 0, marginTop: isExpanded ? 32 : 0 }}
        transition={{ duration: 0.2, delay: isExpanded ? 0 : 0.2 }}
        className={cx(styles.content, {
          [styles.isExpanded]: isExpanded,
        })}
        span={{ [Breakpoint.MOBILE]: 5, [Breakpoint.TABLET]: 5, [Breakpoint.DESKTOP]: 5 }}
        render={<dd ref={ddRef} />}
        onAnimationStart={() => {
          if (!isExpanded) {
            return;
          }
          const el = ddRef.current;
          if (!el) {
            return;
          }
          el.hidden = false;
        }}
        onAnimationEnd={() => {
          if (isExpanded) {
            return;
          }
          const el = ddRef.current;
          if (!el) {
            return;
          }
          // @ts-expect-error React doesn't support the hidden="until-found" attribute or the beforematch event
          el.hidden = 'onbeforematch' in document.body ? 'until-found' : true;
        }}
      >
        <motion.div
          id={`${id}-content`}
          aria-labelledby={`${id}-header`}
          initial={{ opacity: 0 }}
          transition={{ duration: 0.2, delay: isExpanded ? 0.2 : 0 }}
          animate={{ opacity: isExpanded ? 1 : 0 }}
        >
          <Markdown className={typographyStyles.paragraph}>{answer}</Markdown>
        </motion.div>
      </MotionColumn>
    </Grid>
  );
}
